import { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { AlertCircle, Loader2 } from "lucide-react";
import { useChangelog } from "@/hooks/useChangelog";
import ChangelogHeader from "@/components/changelog/ChangelogHeader";
import VersionCard from "@/components/changelog/VersionCard";
import FilterControls from "@/components/changelog/FilterControls";
import { Database } from "@/integrations/supabase/types";
import { useTranslation } from "react-i18next";

type EntryType = Database['public']['Enums']['changelog_entry_type'];

const Changelog = () => {
  const { data: versions, isLoading, error } = useChangelog();
  const { t } = useTranslation('changelog');
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedTypes, setSelectedTypes] = useState<EntryType[]>([]);

  const filteredVersions = useMemo(() => {
    if (!versions) return [];

    return versions.filter(version => {
      // Search filter
      const matchesSearch = !searchTerm || 
        version.version_number.toLowerCase().includes(searchTerm.toLowerCase()) ||
        version.release_notes?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        version.changelog_entries.some(entry => 
          entry.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
          entry.description?.toLowerCase().includes(searchTerm.toLowerCase())
        );

      // Type filter
      const matchesType = selectedTypes.length === 0 ||
        version.changelog_entries.some(entry => 
          selectedTypes.includes(entry.entry_type)
        );

      return matchesSearch && matchesType;
    }).map(version => {
      // Also filter entries within each version
      if (selectedTypes.length > 0) {
        return {
          ...version,
          changelog_entries: version.changelog_entries.filter(entry =>
            selectedTypes.includes(entry.entry_type)
          )
        };
      }
      return version;
    });
  }, [versions, searchTerm, selectedTypes]);

  const handleTypeToggle = (type: EntryType) => {
    setSelectedTypes(prev => 
      prev.includes(type) 
        ? prev.filter(t => t !== type)
        : [...prev, type]
    );
  };

  const handleClearFilters = () => {
    setSearchTerm("");
    setSelectedTypes([]);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background py-8">
        <div className="container mx-auto px-6 max-w-4xl">
          <div className="bg-card rounded-lg shadow-sm p-8">
            <div className="flex items-center justify-center">
              <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
              <span className="ml-3 text-muted-foreground">{t('loading.message')}</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background py-8">
        <div className="container mx-auto px-6 max-w-4xl">
          <div className="bg-card rounded-lg shadow-sm p-8">
            <div className="flex items-center justify-center text-center">
              <AlertCircle className="w-8 h-8 text-destructive mr-3" />
              <div>
                <h2 className="text-lg font-semibold text-foreground mb-2">
                  {t('error.title')}
                </h2>
                <p className="text-muted-foreground mb-4">
                  {t('error.description')}
                </p>
                <Button asChild>
                  <Link to="/">{t('error.backButton')}</Link>
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background py-8">
      <div className="container mx-auto px-6 max-w-4xl">
        <div className="bg-card rounded-lg shadow-sm p-8">
          <ChangelogHeader />
          
          <FilterControls
            searchTerm={searchTerm}
            onSearchChange={setSearchTerm}
            selectedTypes={selectedTypes}
            onTypeToggle={handleTypeToggle}
            onClearFilters={handleClearFilters}
          />

          {filteredVersions && filteredVersions.length > 0 ? (
            <div className="space-y-6">
              {filteredVersions.map((version) => (
                <VersionCard key={version.id} version={version} />
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <AlertCircle className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-foreground mb-2">
                {t('noEntries.title')}
              </h3>
              <p className="text-muted-foreground mb-4">
                {searchTerm || selectedTypes.length > 0 
                  ? t('noEntries.withFilters')
                  : t('noEntries.noData')
                }
              </p>
              {(searchTerm || selectedTypes.length > 0) && (
                <Button variant="outline" onClick={handleClearFilters}>
                  {t('noEntries.resetFilters')}
                </Button>
              )}
            </div>
          )}

          <div className="mt-12 pt-8 border-t">
            <div className="flex flex-col sm:flex-row gap-4">
              <Button asChild>
                <Link to="/">{t('footer.backToHome')}</Link>
              </Button>
              <Button variant="outline" asChild>
                <a href="https://app.uplivion.life/register">{t('footer.tryBeta')}</a>
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Changelog;